import { useState, useEffect, useMemo } from 'react';
import { format } from 'date-fns';
import AuthService from '../services/AuthService';
import DashboardHistoryService from '../services/DashboardHistoryApiService';

const useDashboardHistoryViewModel = (selectedLicense) => {
  const [dateRange, setDateRange] = useState({
    startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000), // Last 30 days
    endDate: new Date()
  });
  const [historyData, setHistoryData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async (selectedLicense) => {
      try {
        setIsLoading(true);
        setError(null);
        if (!selectedLicense || "license_id" in selectedLicense === false) {
          throw new Error('No license selected');
        }

        const data = await DashboardHistoryService.getHistoryData(selectedLicense, dateRange.startDate, dateRange.endDate);
        setHistoryData(data);
      } catch (err) {
        setError(err.message || 'Failed to fetch history data');
        AuthService.logoutWhenError(err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData(selectedLicense);
  }, [selectedLicense, dateRange]);

  const registerNum = useMemo(() => {
    const data = [['Date', 'New Registrations']];
    historyData.forEach(item => {
      data.push([format(new Date(item.timestamp), 'M/d'), item.num_licenses_new_used]);
    });
    return data;
  }, [historyData]);

  const usage = useMemo(() => {
    const data = [['Date', 'Usage']];
    historyData.forEach(item => {
      data.push([format(new Date(item.timestamp), 'M/d'), item.usage_total]);
    });
    return data;
  }, [historyData]);

  const deviceActiveRatio = useMemo(() => {
    const data = [['Date', 'Active Devices']];
    historyData.forEach(item => {
      data.push([format(new Date(item.timestamp), 'M/d'), item.num_licenses_active]);
    });
    return data;
  }, [historyData]);

  const usageDistribution = useMemo(() => {
    const data = [['Usage', 'Count']];
    const usageCounts = new Map();

    historyData.forEach(item => {
      item.usage_distribution.forEach(usageItem => {
        const currentCount = usageCounts.get(usageItem.usage) || 0;
        usageCounts.set(usageItem.usage, currentCount + usageItem.num);
      });
    });

    const sortedUsages = Array.from(usageCounts.entries())
      .sort(([a], [b]) => Number(a) - Number(b));
    
    sortedUsages.forEach(([usage, count]) => {
      data.push([`${usage} times`, count]);
    });
    return data;
  }, [historyData]);

  const platformDistribution = useMemo(() => {
    const data = [['Platform', 'Count']];
    const platformCounts = new Map();

    historyData.forEach(item => {
      item.platform_distribution.forEach(platformItem => {
        const currentCount = platformCounts.get(platformItem.platform) || 0;
        platformCounts.set(platformItem.platform, currentCount + platformItem.num);
      });
    });

    platformCounts.forEach((count, platform) => {
      data.push([platform, count]);
    });
    return data;
  }, [historyData]);

  const appDistribution = useMemo(() => {
    const data = [['App ID', 'Count']];
    const appCounts = new Map();

    historyData.forEach(item => {
      item.app_id_distribution.forEach(appItem => {
        const currentCount = appCounts.get(appItem.app_id) || 0;
        appCounts.set(appItem.app_id, currentCount + appItem.num);
      });
    });

    appCounts.forEach((count, appId) => {
      data.push([appId, count]);
    });
    return data;
  }, [historyData]);

  return {
    dateRange,
    setDateRange,
    isLoading,
    error,
    registerNum,
    usage,
    deviceActiveRatio,
    usageDistribution,
    platformDistribution,
    appDistribution,
  };
};

export default useDashboardHistoryViewModel;